import React from "react";
import styled from "styled-components";
import PanelEmpty from "common/Empty/PanelEmpty";
import useMedia from "utils/hooks/useMedia";
import { Payout, PayoutStatus } from "types/affiliation.types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, PayoutMethodSource } from "@shared/types";
import { formatDate } from "@shared/utils/date";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { checkSearch } from "@shared/utils/misc";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import usePayouts from "features/affiliation/common/hooks/usePayouts";
import { PayoutsStatistics } from "features/affiliation/common/types";
import {
  PAYOUTS_MOBILE,
  PAYOUTS_STATUS_DISPLAY_NAME,
} from "features/affiliation/common/constants";
import Status from "features/affiliation/common/Status";
import PayoutsTable from "features/affiliation/Payouts/PayoutsTable";
import PayoutsActions from "features/affiliation/Payouts/PayoutsActions";
import PayoutMethods from "features/payouts/PayoutMethods/PayoutMethods";
import TabLayout from "features/affiliation/NewLook/TabLayout";
import TabSummary from "features/affiliation/NewLook/TabSummary";
import TablePanel from "features/affiliation/NewLook/TablePanel";
import RowList, {
  Line,
  RowFigure,
  RowMeta,
  RowTitle,
} from "features/affiliation/NewLook/RowList";
import { CreatePayoutButton } from "features/affiliation/NewLook/CreatePayoutButton";
import useAffiliationTotals from "features/affiliation/NewLook/useAffiliationTotals";
import { CardHeader, CardTitle } from "features/affiliation/NewLook/parts";
import {
  formatUsd,
  stripColon,
} from "features/affiliation/NewLook/newLookUtils";

/**
 * The payouts tab in the new look: the balance leads with "Create payout"
 * (this view's primary action), what awaits approval and what was paid
 * beside it, then the payouts and the payout methods.
 */
const NewLookPayouts = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  // Below this width the methods move to the "Manage payout methods" modal.
  const isPayoutsMobile = useMedia(
    [`(max-width: ${PAYOUTS_MOBILE})`],
    [true],
    false
  );
  const { payoutsHistoryQuery, payoutMethodsQuery } = usePayouts();
  const payouts = payoutsHistoryQuery.data;
  const loading = payoutsHistoryQuery.isLoading;
  const totals = useAffiliationTotals();

  // "Create payout" leads the summary, so the empty state doesn't repeat it.
  const emptyState = (
    <PanelEmpty
      title={t("empty.no_payouts")}
      description={t("empty.payouts_description")}
      data-testid="affiliation-empty"
    />
  );
  const searchEmptyState = (
    <PanelEmpty
      title={tCommon("empty.no_results_matching_search", {
        objectName: t("payouts.title").toLowerCase(),
      })}
    />
  );

  const renderRow = (payout: Payout) => (
    <>
      <Line>
        <RowTitle>{payout.payout_method}</RowTitle>
        <RowFigure>{formatUsd(payout.amount)}</RowFigure>
      </Line>
      <Line>
        <RowMeta>{formatDate(payout.time, true, true)}</RowMeta>
        <RowEnd>
          <Status
            itemStatus={PayoutStatus}
            status={payout.status}
            statusDisplayName={t(PAYOUTS_STATUS_DISPLAY_NAME[payout.status])}
          />
          {payout.status === PayoutStatus.PENDING && (
            <PayoutsActions payoutId={payout.payout_id} largeTrigger={true} />
          )}
        </RowEnd>
      </Line>
    </>
  );

  return (
    <TabLayout
      data-testid="affiliation-payouts"
      summary={
        <TabSummary
          loading={totals.loading}
          lead={{
            label: stripColon(t("balance.title")),
            value: totals.balance,
            currency: "$",
            meta: t("overview.balance_hint"),
            action: <CreatePayoutButton primary={true} />,
          }}
          items={[
            {
              key: "awaiting",
              label: t(PayoutsStatistics.AMOUNT_AWAITING_APPROVAL),
              value: formatUsd(totals.awaitingApproval),
              meta: `${t(PayoutsStatistics.PENDING_PAYOUTS)}: ${
                totals.pendingPayouts
              }`,
            },
            {
              key: "paid",
              label: t(PayoutsStatistics.PAID_TO_DATE),
              value: formatUsd(totals.paidOut),
              meta: `${t(PayoutsStatistics.PROCESSED_PAYOUTS)}: ${
                totals.processedPayouts
              }`,
            },
          ]}
        />
      }
      table={
        <TablePanel
          title={t("payouts.title")}
          count={payouts?.length}
          loading={loading}
          table={
            <PayoutsTable
              payouts={payouts}
              loading={false}
              emptyStateComponent={emptyState}
            />
          }
        />
      }
      list={
        <RowList<Payout>
          title={t("payouts.title")}
          items={payouts}
          getKey={(payout) => payout.payout_id}
          renderRow={renderRow}
          filter={(payout, search) => checkSearch(payout.payout_method, search)}
          loading={loading}
          sortOptions={[
            {
              key: "time",
              label: t("payouts.created_date"),
              compare: (a, b) => sortByNumberProperty(a, b, "time"),
            },
            {
              key: "amount",
              label: t("payouts.amount"),
              compare: (a, b) => sortByNumberProperty(a, b, "amount"),
            },
            {
              key: "status",
              label: t("payouts.status"),
              compare: (a, b) => sortByNumberProperty(a, b, "status"),
            },
            {
              key: "payout_method",
              label: t("payouts.method"),
              compare: (a, b) => sortByStringProperty(a, b, "payout_method"),
            },
          ]}
          empty={emptyState}
          searchEmpty={searchEmptyState}
        />
      }
      aside={
        !isPayoutsMobile && (
          <MethodsPanel>
            <PayoutMethods
              payoutMethods={payoutMethodsQuery.data}
              loading={payoutMethodsQuery.isLoading}
              source={PayoutMethodSource.AFFILIATION}
            />
          </MethodsPanel>
        )
      }
    />
  );
};

export default NewLookPayouts;

const RowEnd = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 4px;
`;
/* The payout methods list as a panel: its header becomes the panel's 52px
   header with a hairline, the methods run under it. */
const MethodsPanel = styled.section`
  min-width: 0;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  ${CardHeader} {
    flex-wrap: nowrap;
    min-height: 52px;
    margin: 0;
    padding: 10px 16px 10px 20px;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  ${CardTitle} {
    min-height: 0;
    ${newLookType.panelTitle};
    color: ${(p) => getNewLookTokens(p.theme).ink};
    white-space: nowrap;
  }
  ${CardHeader} + * {
    padding: 12px 16px 4px 20px;
  }
`;
